import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

const OPEN_CONVERSATIONS_KEY = 'mentara_assistant_open_conversations';
const PANEL_OPEN_KEY = 'mentara_assistant_panel_open';

interface AssistantPanelContextType {
  isOpen: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  openConversationIds: string[];
  addOpenConversation: (id: string) => void;
  removeOpenConversation: (id: string) => void;
  clearOpenConversations: () => void;
}

const AssistantPanelContext = createContext<AssistantPanelContextType | null>(null);

// Helper to load persisted open conversations from sessionStorage
function loadOpenConversations(): string[] {
  try {
    const stored = sessionStorage.getItem(OPEN_CONVERSATIONS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

// Helper to save open conversations to sessionStorage
function saveOpenConversations(conversationIds: string[]) {
  try {
    sessionStorage.setItem(OPEN_CONVERSATIONS_KEY, JSON.stringify(conversationIds));
  } catch {
    // Ignore storage errors
  }
}

// Helper to load panel open state from sessionStorage
function loadPanelOpen(): boolean {
  try {
    return sessionStorage.getItem(PANEL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

// Helper to save panel open state to sessionStorage
function savePanelOpen(isOpen: boolean) {
  try {
    sessionStorage.setItem(PANEL_OPEN_KEY, isOpen ? 'true' : 'false');
  } catch {
    // Ignore storage errors
  }
}

export function AssistantPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => loadPanelOpen());
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [openConversationIds, setOpenConversationIds] = useState<string[]>(() => loadOpenConversations());

  // Persist open conversations whenever they change
  useEffect(() => {
    saveOpenConversations(openConversationIds);
  }, [openConversationIds]);

  // Persist panel open state whenever it changes
  useEffect(() => {
    savePanelOpen(isOpen);
  }, [isOpen]);

  const openAssistant = () => setIsOpen(true);
  const closeAssistant = () => {
    setIsOpen(false);
    // Don't clear open conversations on close - let them persist
  };
  const toggleAssistant = () => setIsOpen(prev => !prev);

  const addOpenConversation = (id: string) => {
    if (!openConversationIds.includes(id)) {
      setOpenConversationIds(prev => [...prev, id]);
    }
  };

  const removeOpenConversation = (id: string) => {
    setOpenConversationIds(prev => prev.filter(conversationId => conversationId !== id));
  };

  const clearOpenConversations = () => {
    setOpenConversationIds([]);
  };

  return (
    <AssistantPanelContext.Provider value={{
      isOpen,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      selectedConversationId,
      setSelectedConversationId,
      openConversationIds,
      addOpenConversation,
      removeOpenConversation,
      clearOpenConversations,
    }}>
      {children}
    </AssistantPanelContext.Provider>
  );
}

export function useAssistantPanel() {
  const context = useContext(AssistantPanelContext);
  if (!context) {
    throw new Error('useAssistantPanel must be used within an AssistantPanelProvider');
  }
  return context;
}
